import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/feature/TopNavigation';
import BottomNavigation from '../../components/feature/BottomNavigation';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';

interface InsuranceCard {
  id: string;
  type: string;
  company: string;
  cardNumber: string;
  expiryDate: string;
  holderName: string;
  status: 'active' | 'expired' | 'pending';
  color: string;
}

const mockCards: InsuranceCard[] = [
  {
    id: '1',
    type: 'بطاقة تأمين طبي',
    company: 'شركة التأمين الوطنية',
    cardNumber: '**** **** **** 1234',
    expiryDate: '12/2025',
    holderName: 'أحمد محمد الشيخ',
    status: 'active',
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: '2',
    type: 'بطاقة تأمين عائلي',
    company: 'مجموعة سلامة للتأمين',
    cardNumber: '**** **** **** 5678',
    expiryDate: '08/2024',
    holderName: 'فاطمة أحمد الشيخ',
    status: 'active',
    color: 'from-green-500 to-green-600'
  },
  {
    id: '3',
    type: 'بطاقة تأمين أسنان',
    company: 'التأمين الطبي المتقدم',
    cardNumber: '**** **** **** 9012',
    expiryDate: '03/2024',
    holderName: 'محمد أحمد الشيخ',
    status: 'expired',
    color: 'from-red-500 to-red-600'
  }
];

export default function CardsPage() {
  const navigate = useNavigate();
  const [cards] = useState<InsuranceCard[]>(mockCards);
  const [showAddForm, setShowAddForm] = useState(false);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-50';
      case 'expired': return 'text-red-600 bg-red-50';
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return 'نشطة';
      case 'expired': return 'منتهية الصلاحية';
      case 'pending': return 'قيد المراجعة';
      default: return 'غير محدد';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavigation 
        title="بطاقاتي" 
        showBack 
        onBack={() => navigate('/')}
      />
      
      <div className="pt-20 pb-24 px-4">
        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[#333333] mb-2">بطاقات التأمين</h1>
          <p className="text-[#7A7A7A] text-sm">إدارة جميع بطاقات التأمين الخاصة بك</p>
        </div>

        {/* Add New Card Button */}
        <div className="mb-6">
          <Button
            variant="primary"
            size="lg"
            onClick={() => setShowAddForm(true)}
            className="w-full"
          >
            <i className="ri-add-line text-lg ml-2"></i>
            إضافة بطاقة جديدة
          </Button>
        </div>

        {/* Cards List */}
        <div className="space-y-4">
          {cards.map((card) => (
            <Card key={card.id} padding="none" className="overflow-hidden">
              {/* Card Design */}
              <div className={`bg-gradient-to-r ${card.color} p-6 text-white relative`}>
                <div className="absolute top-4 left-4">
                  <i className="ri-shield-check-fill text-2xl opacity-20"></i>
                </div>
                <div className="absolute top-4 right-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(card.status)}`}>
                    {getStatusText(card.status)}
                  </span>
                </div>
                
                <div className="mt-8">
                  <h3 className="font-bold text-lg mb-1">{card.type}</h3>
                  <p className="text-sm opacity-90 mb-4">{card.company}</p>
                  
                  <div className="mb-4">
                    <p className="text-xl font-mono tracking-wider">{card.cardNumber}</p>
                  </div>
                  
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-xs opacity-75">اسم حامل البطاقة</p>
                      <p className="font-medium">{card.holderName}</p>
                    </div>
                    <div className="text-left">
                      <p className="text-xs opacity-75">تاريخ الانتهاء</p>
                      <p className="font-medium">{card.expiryDate}</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Card Actions */}
              <div className="p-4 bg-white">
                <div className="flex gap-3">
                  <Button variant="outline" size="sm" className="flex-1">
                    <i className="ri-eye-line text-sm ml-1"></i>
                    عرض التفاصيل
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    <i className="ri-download-line text-sm ml-1"></i>
                    تحميل البطاقة
                  </Button>
                  <Button variant="outline" size="sm">
                    <i className="ri-more-line text-sm"></i>
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Add Card Form Modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
            <div className="bg-white w-full rounded-t-3xl p-6 max-h-[80vh] overflow-y-auto">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[#333333]">إضافة بطاقة جديدة</h2>
                <button
                  onClick={() => setShowAddForm(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100"
                >
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">نوع البطاقة</label>
                  <select className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20">
                    <option>بطاقة تأمين طبي</option>
                    <option>بطاقة تأمين عائلي</option>
                    <option>بطاقة تأمين أسنان</option>
                    <option>بطاقة تأمين عيون</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">شركة التأمين</label>
                  <input
                    type="text"
                    placeholder="اسم شركة التأمين"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">رقم البطاقة</label>
                  <input
                    type="text"
                    placeholder="0000 0000 0000 0000"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-2">تاريخ الانتهاء</label>
                    <input
                      type="text"
                      placeholder="MM/YY"
                      className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#333333] mb-2">CVV</label>
                    <input
                      type="text"
                      placeholder="123"
                      className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-2">اسم حامل البطاقة</label>
                  <input
                    type="text"
                    placeholder="الاسم كما هو مكتوب على البطاقة"
                    className="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20"
                  />
                </div>
                
                <div className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center">
                  <i className="ri-camera-line text-3xl text-gray-400 mb-2"></i>
                  <p className="text-sm text-gray-600 mb-2">تصوير البطاقة</p>
                  <Button variant="outline" size="sm">اختيار صورة</Button>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setShowAddForm(false)}
                    className="flex-1"
                  >
                    إلغاء
                  </Button>
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={() => setShowAddForm(false)}
                    className="flex-1"
                  >
                    إضافة البطاقة
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      
      <BottomNavigation />
    </div>
  );
}
